import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Usar o mesmo domínio que a página está sendo servida
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';

const AdminCandidatos = () => {
  const navigate = useNavigate();
  const [candidatos, setCandidatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, pendente: 0, aprovado: 0, rejeitado: 0 });
  const [selectedCandidato, setSelectedCandidato] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [fundadorLink, setFundadorLink] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);

  useEffect(() => {
    loadCandidatos();
  }, [filter]);

  const loadCandidatos = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [candidatosRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/candidatos${filter !== 'all' ? `?status=${filter}` : ''}`, { headers }),
        axios.get(`${API}/admin/candidatos/stats`, { headers })
      ]);
      
      setCandidatos(candidatosRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Erro ao carregar candidatos:', error);
      if (error.response?.status === 403) {
        toast.error('Acesso restrito a administradores');
        navigate('/dashboard');
      } else {
        toast.error('Erro ao carregar candidatos');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAprovarFundador = async (candidatoId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.post(
        `${API}/fundador/admin/aprovar-candidato/${candidatoId}`,
        {},
        { headers }
      );
      
      // Primeiro fecha o modal de detalhes
      setSelectedCandidato(null);
      
      // Depois atualiza a lista
      await loadCandidatos();
      
      // Por fim, mostra o modal do link (usando setTimeout para garantir que o React já processou as mudanças anteriores)
      setTimeout(() => {
        setFundadorLink(response.data);
        setShowLinkModal(true);
        toast.success('Candidato aprovado como Fundador!');
      }, 100);
      
    } catch (error) {
      console.error('Erro ao aprovar como fundador:', error);
      toast.error(error.response?.data?.detail || 'Erro ao aprovar candidato');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejeitar = async (candidatoId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(
        `${API}/admin/candidatos/${candidatoId}/rejeitar`,
        {},
        { headers }
      );
      
      toast.success('Candidato rejeitado');
      setSelectedCandidato(null);
      loadCandidatos();
    } catch (error) {
      console.error('Erro ao rejeitar:', error);
      toast.error('Erro ao rejeitar candidato');
    } finally {
      setActionLoading(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copiado para a área de transferência!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pendente: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      aprovado: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejeitado: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    const labels = {
      pendente: '⏳ Aguardando',
      aprovado: '✅ Aprovado',
      rejeitado: '❌ Rejeitado'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getObjetivoLabel = (objetivo) => {
    return objetivo === 'criador' ? '💰 Criador' : '🔥 Usuário';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando candidatos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      {/* Header */}
      <div className="bg-[#1A1A1A] border-b border-[#D4AF37]/20 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-[#D4AF37]">
                📋 Triagem de Candidatos
              </h1>
              <p className="text-gray-400 mt-1">Aprove candidatos e gere links de Fundador</p>
            </div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-2 bg-[#1A1A1A] hover:bg-[#2E003E]/50 border border-[#D4AF37]/30 rounded-lg text-white transition-all"
            >
              ← Voltar ao Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#1A1A1A] border border-[#D4AF37]/20 rounded-xl p-4">
            <div className="text-3xl font-bold text-white">{stats.total}</div>
            <div className="text-sm text-gray-400">Total</div>
          </div>
          <div className="bg-[#1A1A1A] border border-yellow-500/20 rounded-xl p-4">
            <div className="text-3xl font-bold text-yellow-400">{stats.pendente}</div>
            <div className="text-sm text-gray-400">⏳ Pendentes</div>
          </div>
          <div className="bg-[#1A1A1A] border border-green-500/20 rounded-xl p-4">
            <div className="text-3xl font-bold text-green-400">{stats.aprovado}</div>
            <div className="text-sm text-gray-400">✅ Aprovados</div>
          </div>
          <div className="bg-[#1A1A1A] border border-red-500/20 rounded-xl p-4">
            <div className="text-3xl font-bold text-red-400">{stats.rejeitado}</div>
            <div className="text-sm text-gray-400">❌ Rejeitados</div>
          </div>
        </div>

        {/* Info Box - Sistema de Fundadores */}
        <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#2E003E]/20 border border-[#D4AF37]/30 rounded-xl p-4 mb-6">
          <h3 className="text-[#D4AF37] font-bold mb-2">💎 Sistema de Fundadores</h3>
          <div className="text-sm text-gray-300 space-y-1">
            <p>• Ao aprovar um candidato, será gerado um <strong>Link de Fundador</strong> exclusivo</p>
            <p>• O Fundador recebe <strong>85%</strong> dos ganhos (15% Lyberhot)</p>
            <p>• O Fundador ganha <strong>5% de comissão</strong> sobre todos os criadores que indicar</p>
            <p>• Envie o link via WhatsApp para o candidato criar seu perfil</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { value: 'all', label: 'Todos', icon: '📋' },
            { value: 'pendente', label: 'Pendentes', icon: '⏳' },
            { value: 'aprovado', label: 'Aprovados', icon: '✅' },
            { value: 'rejeitado', label: 'Rejeitados', icon: '❌' }
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                filter === tab.value
                  ? 'bg-[#D4AF37] text-black'
                  : 'bg-[#1A1A1A] text-gray-400 hover:bg-[#2E003E]/50'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Candidatos List */}
        {candidatos.length === 0 ? (
          <div className="bg-[#1A1A1A] border border-[#D4AF37]/20 rounded-xl p-12 text-center">
            <span className="text-6xl block mb-4">📭</span>
            <h3 className="text-xl font-bold text-white mb-2">Nenhum candidato encontrado</h3>
            <p className="text-gray-400">
              {filter === 'all' 
                ? 'Ainda não há aplicações no sistema.' 
                : `Não há candidatos com status "${filter}".`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {candidatos.map((candidato) => (
              <div
                key={candidato.id}
                className="bg-[#1A1A1A] border border-[#D4AF37]/20 rounded-xl p-6 hover:border-[#D4AF37]/50 transition-all"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-bold text-white">{candidato.nome}</h3>
                      {getStatusBadge(candidato.status)}
                      <span className="px-2 py-1 bg-[#2E003E]/30 text-purple-300 text-xs rounded-full">
                        {getObjetivoLabel(candidato.objetivo)}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-400">
                      <div>📱 {candidato.whatsapp}</div>
                      <div>✉️ {candidato.email}</div>
                      {candidato.instagram && <div>📸 @{candidato.instagram}</div>}
                      <div>📅 {formatDate(candidato.created_at)}</div>
                    </div>
                    {candidato.objetivo === 'criador' && candidato.meta_faturamento && (
                      <div className="mt-2 text-sm">
                        <span className="text-[#D4AF37]">💰 Meta:</span>{' '}
                        <span className="text-white">{candidato.meta_faturamento}</span>
                      </div>
                    )}
                    {candidato.fundador_link && (
                      <div className="mt-2 p-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <span className="text-green-400 text-sm">🔗 Link de Fundador gerado</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {candidato.status === 'pendente' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedCandidato(candidato)}
                        className="px-4 py-2 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 border border-[#D4AF37]/50 text-[#D4AF37] rounded-lg font-semibold transition-all"
                      >
                        👁️ Analisar
                      </button>
                    </div>
                  )}
                  {candidato.status === 'aprovado' && candidato.fundador_link && (
                    <button
                      onClick={() => {
                        const baseUrl = window.location.origin;
                        copyLink(`${baseUrl}/fundador/${candidato.fundador_link}`);
                      }}
                      className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 text-green-400 rounded-lg font-semibold transition-all"
                    >
                      📋 Copiar Link
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Analysis Modal */}
      {selectedCandidato && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1A1A1A] border border-[#D4AF37]/30 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-[#D4AF37]/20">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#D4AF37]">📋 Análise de Candidato</h2>
                <button
                  onClick={() => setSelectedCandidato(null)}
                  className="text-gray-500 hover:text-white transition-colors text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <div className="bg-[#0D0D0D] rounded-xl p-4">
                <h3 className="text-lg font-bold text-white mb-4">{selectedCandidato.nome}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">📱 WhatsApp:</span>
                    <a 
                      href={`https://wa.me/55${selectedCandidato.whatsapp?.replace(/\D/g, '') || ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-400 hover:underline"
                    >
                      {selectedCandidato.whatsapp || 'Não informado'}
                    </a>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">✉️ E-mail:</span>
                    <span className="text-white">{selectedCandidato.email}</span>
                  </div>
                  {selectedCandidato.instagram && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">📸 Instagram:</span>
                      <a 
                        href={`https://instagram.com/${selectedCandidato.instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-pink-400 hover:underline"
                      >
                        @{selectedCandidato.instagram}
                      </a>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-400">🎯 Objetivo:</span>
                    <span className="text-white">{getObjetivoLabel(selectedCandidato.objetivo)}</span>
                  </div>
                  {selectedCandidato.meta_faturamento && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">💰 Meta:</span>
                      <span className="text-[#D4AF37]">{selectedCandidato.meta_faturamento}</span>
                    </div>
                  )}
                  {selectedCandidato.busca && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">🔍 Busca:</span>
                      <span className="text-white capitalize">{selectedCandidato.busca}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-400">📅 Aplicou em:</span>
                    <span className="text-white">{formatDate(selectedCandidato.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="flex gap-2">
                {selectedCandidato.whatsapp && (
                  <a
                    href={`https://wa.me/55${selectedCandidato.whatsapp?.replace(/\D/g, '') || ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 px-4 py-3 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-400 rounded-lg text-center font-semibold transition-all"
                  >
                    💬 WhatsApp
                  </a>
                )}
                {selectedCandidato.instagram && (
                  <a
                    href={`https://instagram.com/${selectedCandidato.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 px-4 py-3 bg-pink-500/20 hover:bg-pink-500/30 border border-pink-500/30 text-pink-400 rounded-lg text-center font-semibold transition-all"
                  >
                    📸 Instagram
                  </a>
                )}
              </div>

              {/* Info sobre aprovação */}
              {selectedCandidato.objetivo === 'criador' && (
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4">
                  <h4 className="text-[#D4AF37] font-bold mb-2">💎 Aprovar como Fundador</h4>
                  <p className="text-sm text-gray-300">
                    Ao aprovar, será gerado um link exclusivo para o candidato criar seu perfil de criador com status de <strong>Fundador</strong>.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="p-6 border-t border-[#D4AF37]/20 flex gap-3">
              <button
                onClick={() => handleRejeitar(selectedCandidato.id)}
                disabled={actionLoading}
                className="flex-1 px-4 py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 rounded-lg font-semibold transition-all disabled:opacity-50"
              >
                ❌ Rejeitar
              </button>
              <button
                onClick={() => handleAprovarFundador(selectedCandidato.id)}
                disabled={actionLoading}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-[#D4AF37] to-[#B8962D] hover:shadow-lg hover:shadow-[#D4AF37]/30 text-black rounded-lg font-bold transition-all disabled:opacity-50"
              >
                {actionLoading ? '⏳ Processando...' : '💎 Aprovar Fundador'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link Modal usando Dialog do Shadcn para evitar erros de removeChild */}
      <Dialog open={showLinkModal} onOpenChange={(open) => {
        if (!open) {
          setShowLinkModal(false);
          setFundadorLink(null);
        }
      }}>
        <DialogContent className="bg-[#1A1A1A] border border-[#D4AF37]/30 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-center">
              <span className="text-5xl mb-4 block">🎉</span>
              <span className="text-2xl font-bold text-[#D4AF37]">Link de Fundador Gerado!</span>
            </DialogTitle>
          </DialogHeader>
          
          {fundadorLink && (
            <div className="space-y-4">
              <div className="bg-[#0D0D0D] rounded-xl p-4">
                <p className="text-gray-400 text-sm mb-2">Candidato:</p>
                <p className="text-white font-bold">{fundadorLink.candidato_nome}</p>
                <p className="text-gray-400 text-sm">{fundadorLink.candidato_email}</p>
              </div>

              <div className="bg-[#0D0D0D] rounded-xl p-4">
                <p className="text-gray-400 text-sm mb-2">Link de Fundador:</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={fundadorLink.link_url}
                    readOnly
                    className="flex-1 bg-[#1A1A1A] border border-[#D4AF37]/30 rounded-lg px-3 py-2 text-white text-sm"
                  />
                  <button
                    onClick={() => copyLink(fundadorLink.link_url)}
                    className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg font-semibold hover:bg-[#B8962D] transition-all"
                  >
                    📋 Copiar
                  </button>
                </div>
              </div>

              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <p className="text-green-400 text-sm">
                  <strong>Próximo passo:</strong> Envie este link via WhatsApp para <strong>{fundadorLink.candidato_nome}</strong> criar seu perfil de Fundador.
                </p>
              </div>

              <a
                href={`https://wa.me/55${fundadorLink.candidato_whatsapp?.replace(/\D/g, '')}?text=${encodeURIComponent(`🎉 Parabéns! Sua aplicação foi APROVADA!\n\n💎 Você foi selecionado como FUNDADOR Lyberhot!\n\n🔗 Acesse o link abaixo para criar seu perfil:\n${fundadorLink.link_url}\n\nCom este link você terá:\n✅ 85% dos seus ganhos\n✅ 5% de comissão sobre criadores que indicar\n✅ Perfil verificado automaticamente\n\nBem-vindo(a) ao Lyberhot! 🔥`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full px-4 py-3 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-400 rounded-lg font-semibold transition-all text-center"
              >
                💬 Enviar via WhatsApp
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCandidatos;
